import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { KeyRound, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/** Voluntary password change from the Account page (iter 90).
 *  Unlike ChangePasswordDialog (the forced, non-dismissable dialog for
 *  temp passwords), this one is opened on demand and can be closed. */
export default function AccountChangePassword() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (next.length < 8) {
      setError(t("change_pwd.err_short"));
      return;
    }
    if (next !== confirm) {
      setError(t("change_pwd.err_mismatch"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success(t("account.pwd_success"));
      setCurrent("");
      setNext("");
      setConfirm("");
      setOpen(false);
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) || t("change_pwd.err_generic")
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="bg-white/5 text-white border-white/15 hover:bg-white/10 hover:text-white"
          data-testid="account-change-password-btn"
        >
          <KeyRound size={15} strokeWidth={1.5} className="mr-2" />
          {t("account.pwd_btn")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md bg-zinc-950 border-white/10 text-white">
        <DialogHeader>
          <DialogTitle>{t("account.pwd_btn")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label htmlFor="acc-pwd-current">{t("account.pwd_current")}</Label>
            <Input
              id="acc-pwd-current"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
              data-testid="account-pwd-current-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="acc-pwd-new">{t("account.pwd_new")}</Label>
            <Input
              id="acc-pwd-new"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              required
              data-testid="account-pwd-new-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="acc-pwd-confirm">{t("account.pwd_confirm")}</Label>
            <Input
              id="acc-pwd-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
              data-testid="account-pwd-confirm-input"
            />
          </div>
          {error && (
            <div
              className="flex items-center gap-2 text-sm text-red-400"
              data-testid="account-pwd-error"
            >
              <AlertTriangle size={14} /> {error}
            </div>
          )}
          <Button
            type="submit"
            disabled={busy}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="account-pwd-submit-btn"
          >
            {busy ? (
              <>
                <Loader2 size={15} className="mr-2 animate-spin" />
                {t("account.pwd_saving")}
              </>
            ) : (
              t("account.pwd_save")
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
